import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { displayErrorMessage } from 'redux/notificationBar/notificationBarSlice';
import { updatePatrolReport, updateCheckpointReport, getPatrolDetails, getFileInfo } from 'api/activityReport';
import { formatDateTimeForInput, formatDateTimeForApi, transformChecklists } from './patrolUtils';

const usePatrolActions = ({ onRefetch }) => {
  const dispatch = useDispatch();

  // Expanded row state
  const [expandedPatrolIds, setExpandedPatrolIds] = useState(new Set());
  const [expandedPatrolsData, setExpandedPatrolsData] = useState({});
  const [loadingPatrolIds, setLoadingPatrolIds] = useState(new Set());

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [isLoadingModalDetails, setIsLoadingModalDetails] = useState(false);
  const [isSavingPatrol, setIsSavingPatrol] = useState(false);
  const [patrolEditData, setPatrolEditData] = useState({
    startTime: '',
    endTime: '',
    status: '',
    comment: '',
  });
  const [pendingCheckpointEdits, setPendingCheckpointEdits] = useState({});
  const [hasEdited, setHasEdited] = useState(false);

  // Checklist state (for edit modal)
  const [expandedChecklistId, setExpandedChecklistId] = useState(null);
  const [editingChecklistItemId, setEditingChecklistItemId] = useState(null);
  const [checklistItemEdit, setChecklistItemEdit] = useState({
    selectedStatusId: null, comment: '', time: '', dateTime: null, statusOptions: [],
  });
  const [isSavingChecklistItem, setIsSavingChecklistItem] = useState(false);

  const handlePatrolClick = async (patrol) => {
    const patrolId = patrol.id;

    if (expandedPatrolIds.has(patrolId)) {
      setExpandedPatrolIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(patrolId);
        return newSet;
      });
      return;
    }

    setExpandedPatrolIds((prev) => new Set(prev).add(patrolId));
    setExpandedPatrolsData((prev) => ({ ...prev, [patrolId]: patrol }));

    const hasDataToFetch = patrol.hasChecklist || patrol.hasAttachment;

    if (hasDataToFetch) {
      setLoadingPatrolIds((prev) => new Set(prev).add(patrolId));

      try {
        const promises = [];

        if (patrol.hasChecklist && patrol.scheduleAssignmentId) {
          promises.push(
            getPatrolDetails(patrol.scheduleAssignmentId)
              .then((details) => ({ type: 'checklists', data: details }))
              .catch((err) => {
                console.error('Failed to fetch patrol details:', err);
                return { type: 'checklists', data: null };
              })
          );
        }

        if (patrol.hasAttachment && patrol.attachments?.length > 0) {
          const fileInfoPromises = patrol.attachments.map((attachment) =>
            getFileInfo(attachment.id, attachment.type || 'vv')
              .then((fileInfo) => ({
                ...attachment,
                name: fileInfo?.Name || attachment.name,
                contentType: fileInfo?.ContentType || '',
              }))
              .catch(() => attachment)
          );
          promises.push(
            Promise.all(fileInfoPromises)
              .then((attachments) => ({ type: 'attachments', data: attachments }))
          );
        }

        const results = await Promise.all(promises);
        let updatedData = {};

        results.forEach((result) => {
          if (result.type === 'checklists' && result.data?.Checklists) {
            updatedData.checklists = transformChecklists(result.data.Checklists);
          }
          if (result.type === 'attachments' && result.data) {
            updatedData.attachments = result.data;
          }
        });

        setExpandedPatrolsData((prev) => ({
          ...prev,
          [patrolId]: { ...prev[patrolId], ...updatedData },
        }));
      } catch (error) {
        console.error('Failed to fetch patrol data:', error);
      } finally {
        setLoadingPatrolIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(patrolId);
          return newSet;
        });
      }
    }
  };

  const handleEditPatrol = async (patrol, e) => {
    e.stopPropagation();

    const patrolWithData = expandedPatrolsData[patrol.id] || patrol;

    setSelectedPatrol(patrolWithData);
    setPatrolEditData({
      startTime: formatDateTimeForInput(patrol.startTime),
      endTime: formatDateTimeForInput(patrol.endTime),
      status: patrol.status,
      comment: patrol.comment,
    });
    setExpandedChecklistId(null);
    setPendingCheckpointEdits({});
    setEditingChecklistItemId(null);
    setHasEdited(false);
    setEditModalOpen(true);

    if (patrol.hasChecklist && patrol.scheduleAssignmentId && !patrolWithData.checklists?.length) {
      setIsLoadingModalDetails(true);
      try {
        const details = await getPatrolDetails(patrol.scheduleAssignmentId);
        if (details?.Checklists) {
          setSelectedPatrol((prev) => ({
            ...prev,
            checklists: transformChecklists(details.Checklists),
          }));
        }
      } catch (error) {
        console.error('Failed to fetch patrol details:', error);
      } finally {
        setIsLoadingModalDetails(false);
      }
    }
  };

  const handleSavePatrol = async () => {
    setIsSavingPatrol(true);

    const statusToTitle = {
      clear: 'Clear', warning: 'Attention', emergency: 'Urgent', incomplete: '',
    };

    const updatePayload = {
      PatrolID: selectedPatrol.id,
      Status: statusToTitle[patrolEditData.status] || '',
      Comment: patrolEditData.comment || '',
      StartTime: formatDateTimeForApi(patrolEditData.startTime),
      EndTime: formatDateTimeForApi(patrolEditData.endTime),
    };

    try {
      await updatePatrolReport(updatePayload);

      const checkpointPromises = Object.values(pendingCheckpointEdits).map((checkpointPayload) =>
        updateCheckpointReport(checkpointPayload)
      );

      if (checkpointPromises.length > 0) {
        await Promise.all(checkpointPromises);
      }

      setPendingCheckpointEdits({});
      setEditModalOpen(false);

      if (selectedPatrol) {
        setExpandedPatrolIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(selectedPatrol.id);
          return newSet;
        });
      }
      if (onRefetch) onRefetch();
    } catch (error) {
      console.error('Failed to save patrol:', error);
      dispatch(displayErrorMessage('Failed to save changes. Please try again.'));
    } finally {
      setIsSavingPatrol(false);
    }
  };

  const handleToggleChecklist = (checklistId) => {
    setExpandedChecklistId(expandedChecklistId === checklistId ? null : checklistId);
    setEditingChecklistItemId(null);
  };

  const handleEditChecklistItem = (item) => {
    setEditingChecklistItemId(item.id);
    setChecklistItemEdit({
      selectedStatusId: item.selectedStatusId,
      comment: item.comment || '',
      time: item.time || '',
      dateTime: item.dateTime,
      statusOptions: item.statusOptions || [],
    });
  };

  const deriveNewStatus = (item, selectedStatusId) => {
    const statusMapping = { 1: 'clear', 2: 'warning', 3: 'emergency' };
    const newStatusOption = item.statusOptions?.find(
      (s) => s.StatusID === selectedStatusId
    );
    return newStatusOption?.StatusNumber
      ? statusMapping[newStatusOption.StatusNumber] || 'incomplete'
      : item.status;
  };

  const handleSaveChecklistItem = async () => {
    setIsSavingChecklistItem(true);

    const payload = {
      CheckpointID: editingChecklistItemId,
      Time: checklistItemEdit.time || '',
      DateTime: checklistItemEdit.dateTime,
      Comment: checklistItemEdit.comment || '',
      SelectedStatusID: checklistItemEdit.selectedStatusId,
      SelectedDropdownIDs: [],
    };

    try {
      await updateCheckpointReport(payload);

      setExpandedPatrolsData((prev) => {
        const updated = { ...prev };
        for (const patrolId in updated) {
          const patrol = updated[patrolId];
          if (patrol?.checklists) {
            updated[patrolId] = {
              ...patrol,
              checklists: patrol.checklists.map((checklist) => ({
                ...checklist,
                items: checklist.items.map((item) => {
                  if (item.id !== editingChecklistItemId) return item;
                  return {
                    ...item,
                    comment: checklistItemEdit.comment,
                    selectedStatusId: checklistItemEdit.selectedStatusId,
                    status: deriveNewStatus(item, checklistItemEdit.selectedStatusId),
                  };
                }),
              })),
            };
          }
        }
        return updated;
      });

      setEditingChecklistItemId(null);
      if (onRefetch) onRefetch();
    } catch (error) {
      console.error('Failed to save checkpoint:', error);
      dispatch(displayErrorMessage('Failed to save changes. Please try again.'));
    } finally {
      setIsSavingChecklistItem(false);
    }
  };

  const handleSaveChecklistItemInModal = () => {
    setHasEdited(true);
    setPendingCheckpointEdits((prev) => ({
      ...prev,
      [editingChecklistItemId]: {
        CheckpointID: editingChecklistItemId,
        Time: checklistItemEdit.time || '',
        DateTime: checklistItemEdit.dateTime,
        Comment: checklistItemEdit.comment || '',
        SelectedStatusID: checklistItemEdit.selectedStatusId,
        SelectedDropdownIDs: [],
      },
    }));

    setSelectedPatrol((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        checklists: prev.checklists?.map((checklist) => ({
          ...checklist,
          items: checklist.items.map((item) => {
            if (item.id !== editingChecklistItemId) return item;
            return {
              ...item,
              comment: checklistItemEdit.comment,
              selectedStatusId: checklistItemEdit.selectedStatusId,
              status: deriveNewStatus(item, checklistItemEdit.selectedStatusId),
            };
          }),
        })),
      };
    });

    setEditingChecklistItemId(null);
  };

  const handleCancelChecklistItemEdit = () => {
    setEditingChecklistItemId(null);
    setChecklistItemEdit({ selectedStatusId: null, comment: '', time: '', dateTime: null, statusOptions: [] });
  };

  return {
    expandedPatrolIds,
    setExpandedPatrolIds,
    expandedPatrolsData,
    setExpandedPatrolsData,
    loadingPatrolIds,
    editModalOpen,
    setEditModalOpen,
    selectedPatrol,
    setSelectedPatrol,
    isLoadingModalDetails,
    isSavingPatrol,
    patrolEditData,
    setPatrolEditData,
    pendingCheckpointEdits,
    hasEdited,
    setHasEdited,
    expandedChecklistId,
    editingChecklistItemId,
    checklistItemEdit,
    setChecklistItemEdit,
    isSavingChecklistItem,
    handlePatrolClick,
    handleEditPatrol,
    handleSavePatrol,
    handleToggleChecklist,
    handleEditChecklistItem,
    handleSaveChecklistItem,
    handleSaveChecklistItemInModal,
    handleCancelChecklistItemEdit,
  };
};

export default usePatrolActions;
